import React, { useState } from "react";
import { ShoppingItem } from "../../types/mealPlanning";
import { useToast } from "../../contexts/ToastContext";
import { usePassioApi } from "../../hooks/usePassioApi";
import { tokenUsageTracker } from "../../services/tokenUsageTracker";

interface ShoppingListContentProps {
    shoppingList: ShoppingItem[];
    hasUnsavedChanges?: boolean;
    onRegenerateShoppingList?: () => void;
}

export const ShoppingListContent: React.FC<ShoppingListContentProps> = ({
    shoppingList,
    hasUnsavedChanges,
    onRegenerateShoppingList,
}) => {
    const { showToast } = useToast();
    const { client } = usePassioApi();
    
    // State for affiliate link generation
    const [affiliateLink, setAffiliateLink] = useState<string | null>(null);
    const [affiliateLinkLoading, setAffiliateLinkLoading] = useState(false);
    const [affiliateError, setAffiliateError] = useState<string | null>(null);

    // Track if we've attempted to generate the link to prevent excessive retries
    const [hasAttempted, setHasAttempted] = React.useState(false);
    const [isGeneratingLink, setIsGeneratingLink] = React.useState(false);

    // Function to convert shopping list to affiliate link
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const convertFromShoppingList = async (items: ShoppingItem[]) => {
        if (!client) return;
        
        try {
            setAffiliateLinkLoading(true);
            setAffiliateError(null);
            const result = await client.convertMealPlanToAffiliateLink(items);
            setAffiliateLink(result.products_link_url);
            return result;
        } catch (error: any) {
            setAffiliateError(error.message || 'Failed to generate affiliate link');
            throw error;
        } finally {
            setAffiliateLinkLoading(false);
        }
    };

    // Memoized affiliate link generation with error handling and retry prevention
    React.useEffect(() => {
        // Only attempt once per shopping list, and only if we haven't already tried or succeeded
        const shouldAttempt =
            shoppingList.length > 0 &&
            !hasAttempted &&
            !affiliateLinkLoading &&
            !affiliateLink &&
            !affiliateError &&
            !isGeneratingLink &&
            client;

        if (shouldAttempt) {
            setHasAttempted(true);
            setIsGeneratingLink(true);

            // Make the call non-blocking and with proper error handling
            convertFromShoppingList(shoppingList)
                .then(() => {
                    // Check if we have token usage data
                    const usageData = tokenUsageTracker.getUsageData();
                    if (usageData && usageData.requestUsage > 500) {
                        // Show token usage in a toast
                        setTimeout(() => {
                            showToast({
                                message: `Used ${usageData.requestUsage.toLocaleString()} tokens for Instacart link`,
                                type: "info",
                                duration: 5000,
                            });
                        }, 500);
                    }
                })
                .catch((error: any) => {
                    console.warn(
                        "Failed to generate Instacart affiliate link:",
                        error
                    );
                    // Don't block the UI - just log the error
                })
                .finally(() => {
                    setIsGeneratingLink(false);
                });
        }
    }, [
        shoppingList.length,
        hasAttempted,
        affiliateLinkLoading,
        affiliateLink,
        affiliateError,
        isGeneratingLink,
        shoppingList,
        showToast,
        client,
        convertFromShoppingList,
    ]);

    // Reset attempt flag when shopping list changes significantly
    const shoppingListString = JSON.stringify(shoppingList);
    React.useEffect(() => {
        setHasAttempted(false);
        setIsGeneratingLink(false);
    }, [shoppingListString]);

    const containerStyle: React.CSSProperties = {
        backgroundColor: "white",
        borderRadius: "8px",
        padding: "40px",
        maxWidth: "800px",
        margin: "0 auto",
        fontFamily: "system-ui, -apple-system, sans-serif",
    };

    const listStyle: React.CSSProperties = {
        listStyleType: "none",
        padding: 0,
        margin: 0,
    };

    const itemStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        borderBottom: "1px solid #f3f4f6",
        fontSize: "16px",
    };

    const itemNameStyle: React.CSSProperties = {
        fontWeight: "500",
        color: "#1a202c",
        flex: 1,
    };

    const itemQuantityStyle: React.CSSProperties = {
        color: "#6b7280",
        fontWeight: "400",
        minWidth: "120px",
        textAlign: "right",
    };

    const footerStyle: React.CSSProperties = {
        marginTop: "40px",
        paddingTop: "24px",
        borderTop: "2px solid #e5e7eb",
        textAlign: "center",
    };

    const instacartLinkStyle: React.CSSProperties = {
        display: "inline-block",
        padding: "12px 24px",
        backgroundColor: "#00b341",
        color: "white",
        textDecoration: "none",
        borderRadius: "8px",
        fontWeight: "600",
        fontSize: "16px",
        transition: "all 0.2s",
    };

    const loadingStyle: React.CSSProperties = {
        color: "#6b7280",
        fontStyle: "italic",
    };

    return (
        <>
            {/* Print-friendly styles */}
            <style>
                {`
                    @media print {
                        .shopping-list-container {
                            border-radius: 0 !important;
                            box-shadow: none !important;
                            padding: 20px !important;
                            max-width: 100% !important;
                            margin: 0 !important;
                        }
                        .shopping-list-footer {
                            display: none !important;
                        }
                    }
                `}
            </style>
            <div style={containerStyle} className="shopping-list-container">
                <div style={{
                    position: "relative",
                    marginBottom: "40px",
                    borderBottom: "2px solid #e5e7eb",
                    paddingBottom: "20px"
                }}>
                    <button
                        onClick={onRegenerateShoppingList}
                        style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            padding: "12px 20px",
                            backgroundColor: "#3b82f6",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            transition: "all 0.2s ease",
                            boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#2563eb";
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#3b82f6";
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 2px 4px rgba(59, 130, 246, 0.2)";
                        }}
                    >
                        🔄 {shoppingList.length === 0 ? "Generate" : "Regenerate"}
                    </button>
                    <h1 style={{
                        fontSize: "32px",
                        fontWeight: "600",
                        color: "#1a202c",
                        marginBottom: "8px",
                        textAlign: "center"
                    }}>🛒 Shopping List</h1>
                    <p style={{
                        fontSize: "16px",
                        color: "#6b7280",
                        textAlign: "center"
                    }}>
                        Everything you need for your meal plan ({shoppingList.length} items)
                    </p>
                </div>

                {hasUnsavedChanges && (
                    <div
                        style={{
                            backgroundColor: "#fef3c7",
                            border: "1px solid #fcd34d",
                            borderRadius: "8px",
                            padding: "16px",
                            marginBottom: "24px",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "14px",
                                color: "#92400e",
                                fontWeight: "500",
                                margin: "0",
                                textAlign: "center"
                            }}
                        >
                            ⚠️ Shopping list may be outdated due to unsaved changes
                        </p>
                    </div>
                )}

                {shoppingList.length === 0 ? (
                    <div style={{
                        textAlign: "center",
                        padding: "60px 20px",
                        color: "#6b7280",
                    }}>
                        <div style={{
                            fontSize: "48px",
                            marginBottom: "16px",
                            opacity: 0.5,
                        }}>
                            📋
                        </div>
                        <div style={{
                            fontSize: "18px",
                            fontWeight: "500",
                            marginBottom: "8px",
                        }}>
                            No shopping list generated yet
                        </div>
                        <div style={{
                            fontSize: "14px",
                            color: "#9ca3af",
                        }}>
                            Click "Regenerate" above to create a shopping list from your meal plan
                        </div>
                    </div>
                ) : (
                    <ul style={listStyle}>
                        {shoppingList.map((item, index) => (
                            <li key={index} style={itemStyle}>
                                <span style={itemNameStyle}>{item.name}</span>
                                <span style={itemQuantityStyle}>
                                    {item.portionQuantity} {item.portionSize}
                                    {item.portionQuantity !== 1 &&
                                    !item.portionSize.endsWith("s")
                                        ? "s"
                                        : ""}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {shoppingList.length > 0 && (
                    <div style={footerStyle} className="shopping-list-footer">
                        {affiliateLinkLoading || isGeneratingLink ? (
                            <div style={loadingStyle}>
                                Generating Instacart shopping link...
                            </div>
                        ) : affiliateLink ? (
                            <a
                                href={affiliateLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={instacartLinkStyle}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        "#00a83a";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        "#00b341";
                                }}
                            >
                                🛒 Shop on Instacart
                            </a>
                        ) : affiliateError ? (
                            <div style={loadingStyle}>
                                Instacart link temporarily unavailable
                            </div>
                        ) : (
                            <div style={loadingStyle}>
                                Instacart link not available
                            </div>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}; 